/* eslint-disable max-lines */
import { isIOS, isMobile, isFirefox } from 'react-device-detect';
import { observable } from 'mobx';
import moment from 'moment';

import logger from 'logger';
import CookieStorageService from 'services/CookieStorageService';
import LocalStorageService from 'services/LocalStorageService';
import createMessagingService from 'services/MessagingService/MessagingService';
import { HLS_DEBUG, HLS_RECONNECT } from 'services/MessagingService/EventNames';
import AdStore from 'stores/AdStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import allUserGroups from 'enum/userGroups';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import { BROADCAST_STATUS, STREAM_VOLUME_KEY } from 'enum/videoPlayer';
import trackingService from 'services/UITrackingService';


const EXPIRY_TIME_OFFSET = 12;

class VideoAdService {
  /**
   * Represents a VideoAdService
   * @constructor
   */
  constructor() {
    this.messagingService = createMessagingService();
    this.IMA_SDK_ID = 'ima3-sdk-lib';
    this.adsInitialized = false;
    this.adsLoaded = false;
    this.autoplayAllowed = true;
    this.autoplayRequiresMuted = true;
    this.adDisplayContainer = undefined;
    this.adContainer = undefined;
    this.countDownContainer = undefined;
    this.adsLoader = undefined;
    this.adsManager = undefined;
    this.videoContent = undefined;
    // seconds
    this.cooldownTime = 120;

    this.adLinks = {
      preroll: {
        desktop: {
          straight: '//s.st1net.com/splash.php?idzone=212',
          gay: '//s.st1net.com/splash.php?idzone=214',
          trans: '//s.st1net.com/splash.php?idzone=216',
        },
        mobile: {
          straight: '//s.st1net.com/splash.php?idzone=218',
          gay: '//s.st1net.com/splash.php?idzone=220',
          trans: '//s.st1net.com/splash.php?idzone=222',
        },
      },
    };
  }

  @observable onResume = null;
  @observable isAdOver = false;
  @observable progressValue = 0;
  @observable isAdRunning = false;
  @observable url = null;

  /**
   * Method for importing IMA library
   * @param {function} [onLoadCallback] callback function
   */
  importIMASDK(onLoadCallback) {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::importIMASDK' });
    if (!this.scriptLoaded) {
      const imaScript = document.createElement('script');
      imaScript.onerror = this.loadError;
      if (typeof onLoadCallback === 'function') {
        imaScript.onload = onLoadCallback;
      }
      imaScript.id = this.IMA_SDK_ID;
      document.body.appendChild(imaScript);
      imaScript.src = '//imasdk.googleapis.com/js/sdkloader/ima3.js';
    }
  }

  /**
   * Getter for script loaded status
   * @returns script element if exists - otherwise undefined
   */
  get scriptLoaded() {
    return !!(document.getElementById(this.IMA_SDK_ID) && window.google && window.google.ima);
  }

  /**
   * Method for handling IMA import error
   * @throws {URIError}
   */
  loadError = (error) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::adsLoadError', error });
    this.isAdOver = true;
    if (this.onResume) {
      this.onResume();
    } else {
      this.messagingService.emit(HLS_RECONNECT);
    }
    throw new URIError(`The script ${error.target.src} didn't load correctly.`);
  };

  /**
   * Main method for start video ads
   * @param {DOMElement} videoElement video element that is used for displaying content
   */
  start(videoElement, ownPlayer, onResume) {
    // onResume custom optional callback that will be used instead of HLS_RECONNECT emit
    // needs for jink-player stuff
    this.onResume = onResume;
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::start', videoElement, ownPlayer });
    if (ownPlayer) {
      this.playerContainer = ownPlayer;
    }
    // get ref to video content
    if (videoElement) {
      this.videoContent = videoElement;
      videoElement.volume = this.getVolume();
      this.initializeIMA(ownPlayer);
    }
  }

  getVolume = () => {
    const defaultVolume = 0.5;
    const volume = LocalStorageService.get(STREAM_VOLUME_KEY);
    if (!volume) {
      return defaultVolume;
    }

    let formattedVolume = Number(volume);
    if (isNaN(formattedVolume)) {
      return defaultVolume;
    }

    if (formattedVolume > 1) {
      formattedVolume /= 100;
    }

    if (0 <= formattedVolume && formattedVolume <= 1) {
      return formattedVolume;
    }

    return defaultVolume;
  };

  /**
   * Initialize ads loader and set some event listeners
   */
  initializeIMA = (ownPlayer) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::initializeIMA', ownPlayer });
    // Prevent this function from running if no script is loaded
    if (!this.scriptLoaded || !this.videoContent || this.adsLoaded) {
      return;
    }

    if (isIOS) {
      window.google.ima.settings.setDisableCustomPlaybackForIOS10Plus(true);
    }

    this.adContainer = document.getElementById('video-ad-container');
    this.countDownContainer = document.getElementById('video-ad-countdown-container');

    if (!this.adContainer) {
      this.onContentResumeRequested();

      return;
    }

    this.adDisplayContainer = new window.google.ima.AdDisplayContainer(this.adContainer, this.videoContent);
    this.adsLoader = new window.google.ima.AdsLoader(this.adDisplayContainer);
    this.adsLoader.addEventListener(
      window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      this.onAdsManagerLoaded,
      false
    );
    this.adsLoader.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, this.onAdError, false);

    this.adsLoaded = true;
    this.loadOrPlay(ownPlayer);
  };

  loadOrPlay(load) {
    if (load) {
      this.loadAds();
    } else {
      this.playVideo();
    }
  }

  inferIframeClick = () => {
    const iframe = this.adContainer.querySelector('iframe');

    if (
      document.activeElement?.nodeName.toLowerCase() === 'iframe' &&
      iframe === document.activeElement
    ) {
      trackingService.emit('adClick', this.getTrackingPayload());
    }
  };

  playVideo() {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::playVideo::requested' });
    // eslint-disable-next-line no-console
    console.log('trying to play video ads with sound...');
    this.videoContent
      .play()
      .then(() => {
        this.messagingService.emit(HLS_DEBUG, { method: 'ads::playVideo::success' });
        this.autoplayAllowed = true;
        this.autoplayRequiresMuted = false;
        this.loadAds();
        // eslint-disable-next-line no-console
        console.log('success! Video ads can be played with sound');
      })
      .catch(() => {
        this.messagingService.emit(HLS_DEBUG, { method: 'ads::playVideo::error' });
        // eslint-disable-next-line no-console
        console.log('denied, trying to play video ads muted now...');
        this.playVideoMuted();
      });
  }

  playVideoMuted() {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::playVideoMuted::requested' });
    this.videoContent.muted = true;
    this.videoContent
      .play()
      .then(() => {
        this.messagingService.emit(HLS_DEBUG, { method: 'ads::playVideoMuted::success' });
        this.autoplayAllowed = true;
        this.autoplayRequiresMuted = true;
        this.loadAds();
        // eslint-disable-next-line no-console
        console.log('success! Video ads can be played muted');
      })
      .catch((error) => {
        this.messagingService.emit(HLS_DEBUG, { method: 'ads::playVideoMuted::error' });
        this.onAdError(error);
      });
  }

  /**
   * @callback
   * Fires up when ad manager is loaded
   * It will instantiate adsManager from the adsLoader response
   * Add listeners for adsManager events
   * Tries to play video ads
   * @param {Event} adsManagerLoadedEvent custom event from IMA library
   */
  onAdsManagerLoaded = (adsManagerLoadedEvent) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::onAdsManagerLoaded', adsManagerLoadedEvent });
    const adsRenderingSettings = new window.google.ima.AdsRenderingSettings();
    adsRenderingSettings.uiElements = [
      window.google.ima.UiElements.AD_ATTRIBUTION,
      window.google.ima.UiElements.COUNTDOWN,
    ];
    this.adsManager = adsManagerLoadedEvent.getAdsManager(this.videoContent);

    // Add listeners to the required events.
    this.adsManager.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, this.onAdError);
    this.adsManager.addEventListener(
      window.google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
      this.onContentPauseRequested
    );
    this.adsManager.addEventListener(window.google.ima.AdEvent.Type.AD_PROGRESS, this.onAdProgress);
    this.adsManager.addEventListener(
      window.google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.onContentResumeRequested
    );
    // for non linear ads
    this.adsManager.addEventListener(window.google.ima.AdEvent.Type.LOADED, this.onAdLoaded);
    this.adsManager.addEventListener(window.google.ima.AdEvent.Type.PAUSED, () => {
      this.messagingService.emit(HLS_DEBUG, { method: 'ads::onAdPaused' });
      if (this.updateProgressBarInterval) {
        clearInterval(this.updateProgressBarInterval);
      }
      this.adsManager.resume();
    });

    this.adsManager.addEventListener(window.google.ima.AdEvent.Type.RESUMED, (e) => {
      this.messagingService.emit(HLS_DEBUG, { method: 'ads::onAdResumed' });
      if (this.updateProgressBarInterval) {
        clearInterval(this.updateProgressBarInterval);
      }
      const duration = e && e.getAd() && e.getAd().getDuration();
      if (duration) {
        this.updateProgressBarInterval = setInterval(() => {
          this.progressValue = Math.min(this.progressValue + (100 / (duration * 10)), 100);
        }, 100);
      }
    });

    // play ads if autoplay is allowed
    if (this.autoplayAllowed) {
      this.playAds();
    }
  };

  getWidth() {
    return this.playerContainer ? this.playerContainer.offsetWidth : this.videoContent.offsetWidth;
  }

  getHeight() {
    return this.playerContainer ? this.playerContainer.offsetHeight : this.videoContent.offsetHeight;
  }

  /**
   * Method for starting video ads
   * It will add necessary settings and try to start ad
   * If playback error it will be catched
   */
  playAds() {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::playAds' });
    try {
      if (!this.adsInitialized) {
        this.initializeAds();
      }
      // Initialize the ads manager. Ad rules playlist will start at this time.

      this.adsManager.init(this.getWidth(), this.getHeight(), window.google.ima.ViewMode.NORMAL);
      // Call start to show ads. Single video and overlay ads will
      // start at this time; this call will be ignored for ad rules, as ad rules
      // ads start when the adsManager is initialized.

      this.adsManager.start();

      window.addEventListener('resize', () => {
        this.adsManager.resize(this.getWidth(), this.getHeight(), window.google.ima.ViewMode.NORMAL);
      });
    } catch (adError) {
      // An error may be thrown if there was a problem with the VAST response.
      // Play content here, because we won't be getting an ad.
      logger.error({ msg: 'Error with VAST response', error: adError });
      this.onAdError(adError);
    }
  }

  /**
   * @callback
   * This callback will handle if ad is non-linear - companion ad
   * @param {Event} adEvent custom event from IMA library
   */
  onAdLoaded = (adEvent) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::onAdLoaded' });
    const ad = adEvent.getAd();
    if (!ad.isLinear()) {
      this.videoContent.play();
    }

    this.url = adEvent.getAdData()?.clickThroughUrl;
  };

  /**
   * @callback
   * Fires up when ad starts playing
   */
  onContentPauseRequested = (e) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::onContentPauseRequested' });
    this.adContainer.classList.add('visible');
    if (this.countDownContainer) {
      this.countDownContainer.classList.add('visible');
    }
    if (e && e.getAd() && e.getAd().getDuration()) {
      this.setupProgressBar(e.getAd().getDuration());
    }
    const expiry = moment().add(EXPIRY_TIME_OFFSET, 'hours').toDate();
    CookieStorageService.set('cam4_video_ad_cooldown', ProfileStore.profileName, {
      path: '/',
      expires: expiry,
    });

    LocalStorageService.set('videoAdsTimestamp', Date.now());

    this.isAdRunning = true;

    window.addEventListener('blur', this.inferIframeClick);
    trackingService.emit('adLoaded', this.getTrackingPayload({
      event: e,
      container: this.adContainer,
    }));
  };

  onAdProgress = (e) => {
    if (e && e.getAdData()) {
      const timeRemaining = Math.ceil(e.getAdData().duration - e.getAdData().currentTime);
      if (this.countDownContainer) {
        this.countDownContainer.innerHTML = `Ad - ${timeRemaining}`;
      }
    }
  };

  setupProgressBar = (duration) => {
    if (this.updateProgressBarInterval) {
      clearInterval(this.updateProgressBarInterval);
      this.progressValue = 0;
    }
    this.updateProgressBarInterval = setInterval(() => {
      this.progressValue = Math.min(this.progressValue + (100 / (duration * 10)), 100);
    }, 100);
  };

  /**
   * @callback
   * Fires up when ad finishes playing
   */
  onContentResumeRequested = () => {
    this.messagingService.emit(HLS_DEBUG, {
      method: 'ads::onContentResumeRequested',
      playerContainer: this.playerContainer,
    });
    this.cleanUpUI();
    if (this.updateProgressBarInterval) {
      clearInterval(this.updateProgressBarInterval);
    }
    this.isAdRunning = false;
    this.isAdOver = true;

    if (this.onResume) {
      this.onResume();
    } else if (this.playerContainer) {
      this.messagingService.emit(HLS_RECONNECT);
    }

    trackingService.emit('adClose', this.getTrackingPayload());
    window.removeEventListener('blur', this.inferIframeClick);
  };

  /**
   * @callback
   * Fires up when error ocurs when we try to play video ad
   * Logs error and clean up UI
   * Resumes video content
   * @param {Event} adErrorEvent custom event from IMA library
   */
  onAdError = (adErrorEvent) => {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::onAdError' });
    // Handle the error logging.
    const error = adErrorEvent?.getError?.() || 'videoAdService error';
    logger.error(error);
    trackingService.emit('adError', this.getTrackingPayload({ error }));

    if (this.adsManager) {
      this.adsManager.destroy?.();
    }

    this.onContentResumeRequested(true);
  };

  /**
   * Method for resuming video content
   */
  resumeVideoContent = () => {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::resumeVideoContent' });
    this.videoContent.play();

    trackingService.emit('adResumed', this.getTrackingPayload());
  };

  /**
   * Helper method to clean up UI usually before resuming video content
   */
  cleanUpUI = () => {
    // this will be needed for later use
    // this.removeUpgradeToGoldButton();
    // this.removeBuyTokensButton();
    if (this.adContainer) {
      this.adContainer.classList.remove('visible');
    }
    if (this.countDownContainer) {
      this.countDownContainer.classList.remove('visible');
    }
  };

  /**
   * Method for starting ad request after initial setup
   * It will instantiate new adsRequest
   * It will add tag id and width and height
   * And call method on adsLoader that will request ads
   */
  startAdRequest() {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::startAdRequest' });
    const adsRequest = new window.google.ima.AdsRequest();
    adsRequest.adTagUrl = this.adLinks.preroll[isMobile ? 'mobile' : 'desktop'][AdStore.genderType];

    // Specify the linear and nonlinear slot sizes. This helps the SDK to
    // select the correct creative if multiple are returned.
    adsRequest.linearAdSlotWidth = this.videoContent.clientWidth;
    adsRequest.linearAdSlotHeight = this.videoContent.clientHeight;
    adsRequest.nonLinearAdSlotWidth = this.videoContent.clientWidth;
    adsRequest.nonLinearAdSlotHeight = this.videoContent.clientHeight / 3;
    adsRequest.setAdWillAutoPlay(this.autoplayAllowed);
    adsRequest.setAdWillPlayMuted(this.autoplayRequiresMuted);

    this.adsLoader.requestAds(adsRequest);
  }

  /**
   * Method for initialize ad display container
   * Must be done as the result of a user action on mobile
   */
  initializeAds() {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::initializeAds' });
    this.adDisplayContainer.initialize();
    this.adsInitialized = true;
  }

  /**
   * Method that will prevent content playback and start ad request
   * as start for loading ads
   * @param {event} event used for preventing immediate playback when ads are loading
   */
  loadAds(event) {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::loadAds' });
    event?.preventDefault?.();
    this.startAdRequest();
  }

  /**
   *
   * @param {object} user logged in user
   * @param {string} bStatus broadcast status
   * @returns {boolean} True or false value for showing video ads
   */
  shouldDisplayVideoAds = (user, bStatus, isCompanionAd) => {
    const isSameCam = CookieStorageService.get('cam4_video_ad_cooldown') === ProfileStore.profileName;
    const adsTimestamp = LocalStorageService.get('videoAdsTimestamp') || 0;
    const adsCooldown = Date.now() - adsTimestamp;
    const isCooldownActive = adsCooldown < this.cooldownTime * SECOND_AS_MILLISECONDS;
    const isTier3Country = UserStore.hasCF(allUserGroups.REMOVE_ALL_ADS);

    if (isTier3Country) {
      return false;
    }

    if ((isSameCam || isCooldownActive) && !isCompanionAd) {
      return false;
    }

    if (user) {
      const isSfw = user.user.getIn(['user', 'sfw']);
      if (
        user.isPerformer ||
        user.accessLevel >= ACCESS_LEVEL_NUM.GOLD ||
        isSfw ||
        isFirefox ||
        bStatus === BROADCAST_STATUS.ACCEPTED_INSIDE_PS ||
        bStatus === BROADCAST_STATUS.INSIDE_GS ||
        bStatus === BROADCAST_STATUS.SPYING
      ) {
        return false;
      }
    }

    return true;
  };

  /**
   * Method that will stop current ad and all loaded ads
   * Also removes ad tracking
   */
  destroy = () => {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::destory' });
    if (this.adsManager) {
      this.adsManager.destroy();
      this.onContentResumeRequested();
    }
  };

  reset = () => {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::reset' });
    this.isAdOver = false;
    this.progressValue = 0;
    this.isAdRunning = false;
    this.adsLoaded = false;
    this.adsInitialized = false;
    this.url = null;
    if (this.updateProgressBarInterval) {
      clearInterval(this.updateProgressBarInterval);
    }
    if (this.adsManager) {
      this.adsManager.destroy();
    }
  };

  pauseVideoAd = () => {
    this.messagingService.emit(HLS_DEBUG, { method: 'ads::pauseVideoAd' });
    this.adsManager.pause();

    trackingService.emit('adPaused', this.getTrackingPayload());
  };

  getTrackingPayload = (details) => {
    const container = details?.container;

    const ad = details?.event?.getAd();
    const adDetails = {
      ...ad && {
        id: ad.getAdId(),
        title: ad.getTitle(),
        system: ad.getAdSystem(),
        advertiser: ad.getAdvertiserName(),
        type: ad.getContentType(),
        creativeId: ad.getCreativeId(),
        description: ad.getDescription(),
        duration: ad.getDuration(),
        url: ad.getMediaUrl(),
        skipOffset: ad.getSkipTimeOffset(),
        skippable: ad.isSkippable(),
        clickThroughUrl: (
          ad.h?.clickThroughUrl
          || ad.j?.clickThroughUrl
        ),
        ...container && { container },
      },
    };

    const payload = {
      ...adDetails,
      adType: 'video',
    };

    return payload;
  };
}

export default new VideoAdService();
